﻿import _ from "lodash";
import addRetryLogicToFetch from "fetch-retry";
import { attachToEnvironment as attachFetchInterceptorToEnvironment } from "./services/fetchInterceptService";
import { default as Color } from "colorjs.io";
import moment, { Moment } from "moment";
import { UTCDate } from "@date-fns/utc";
import { DateOnly } from "./utilities";


// Make lodash available globally...
window._ = _;

// Backwards-compatibility - Make colorjs.io available globally (this library was originally added before it was available as a module, so it was globally available)...
window["Color"] = Color;

/// Use ISO 8601 format for all serialized dates...
Date.prototype.toString = Date.prototype.toISOString; // eslint-disable-line no-extend-native

export type RequestInitWithRetries = RequestInit & {
    retries?: number;
    retryDelay?: (attempt: number, error: Error, response: Response) => number;
    retryOn?: number[] | ((attempt: number, error: Error, response: Response) => boolean);
    timeout?: number;
};

/// Monkey-patch fetch() to retry failed requests and allow request interception...
const { fetch: originalFetch } = window;
const fetchWithTimeout = async(url, options: RequestInitWithRetries) => {
    // If no timeout is specified, just use the original fetch...
    if (!options.timeout) {
        return originalFetch(url, options);
    }

    // Else, create an abort signal and use it to return a 408 "response" instead (which the retryOn logic can then use)...
    const abortSignal = AbortSignal.timeout(options.timeout);
    const response = await originalFetch(url, { ...options, signal: abortSignal });
    if (abortSignal.aborted) {
        return ({ status: 408, statusText: "Request Timeout", headers: response.headers, body: response.body } as Response);
    } else {
        return response;
    }
};
const fetchWithRetry = addRetryLogicToFetch(fetchWithTimeout);
const defaultRetryOptions = {
    retries: 3,
    // Exponential backoff...
    retryDelay: function (attempt, _error, _response) {
        return Math.pow(2, attempt) * 1000; // 1000, 2000, 4000
    },
    retryOn: [
        408, // Request Timeout
        429, // Too Many Requests
        500, // Internal Server Error
        502, // Bad Gateway
        503, // Service Unavailable
        504, // Gateway Timeout
    ]
};
window.fetch = (url, options) => fetchWithRetry(url, { ...defaultRetryOptions, ...options }); // Use default retry options, with any overrides from the caller...
const fetchIntercept = attachFetchInterceptorToEnvironment(window);
export { fetchIntercept };

/// By default, strip all React/DOM elements, functions and symbols when serializing JSON data.
/// Some third-party libraries (react-grid) are using raw JSON.stringify calls for change tracking, so any JSX in table cells will cause a circular reference error.
/// This default implementation of the replacer function prevents circular reference errors from occuring and improves serialization performance in some cases.
const originalStringify = JSON.stringify;
JSON.stringify = (value: any, replacer?: (number | string)[] | ((this: any, key: string, value: any) => any), space?: string | number) => {
    if (!!replacer) {
        return originalStringify(value, replacer as any, space);
    }

    function transientEntryReplacer(_key, value) {
        if (_.isFunction(value) || _.isElement(value) || _.isSymbol(value) || _.has(value, "$$typeof")) {
            return undefined;
        }

        return value;
    };

    return originalStringify(value, transientEntryReplacer, space);
};


/// Monkey-patch moment with Date to support backward compatibility
declare global {
    interface Date {
        // Core Moment methods
        format(template?: string): string;
        isValid(): boolean;
        clone(): Moment;
        toDate(): Date;
        valueOf(): number;
        unix(): number;
        utc(keepLocalTime?: boolean): Moment;
        local(keepLocalTime?: boolean): Moment;

        // Manipulation methods
        add(amount?: moment.DurationInputArg1, unit?: moment.DurationInputArg2): Moment;
        subtract(amount?: moment.DurationInputArg1, unit?: moment.DurationInputArg2): Moment;
        startOf(unitOfTime: moment.unitOfTime.StartOf): Moment;
        endOf(unitOfTime: moment.unitOfTime.StartOf): Moment;

        // Query methods
        isBefore(inp?: moment.MomentInput, granularity?: moment.unitOfTime.StartOf): boolean;
        isAfter(inp?: moment.MomentInput, granularity?: moment.unitOfTime.StartOf): boolean;
        isSame(inp?: moment.MomentInput, granularity?: moment.unitOfTime.StartOf): boolean;
        isBetween(a: moment.MomentInput, b: moment.MomentInput, granularity?: moment.unitOfTime.StartOf, inclusivity?: "()" | "[)" | "(]" | "[]"): boolean;

        // Difference methods
        diff(b: moment.MomentInput, unitOfTime?: moment.unitOfTime.Diff, precise?: boolean): number;

        // Get/Set methods
        year(): number;
        year(value: number): Moment;
        month(): number;
        month(value: number): Moment;
        date(): number;
        date(value: number): Moment;
        day(): number;
        day(value: number): Moment;
        hour(): number;
        hour(value: number): Moment;
        minute(): number;
        minute(value: number): Moment;
        second(): number;
        second(value: number): Moment;
        millisecond(): number;
        millisecond(value: number): Moment;
        week(): number;
        week(value: number): Moment;
        /**
         * Removes the time offset from a date by adjusting the date's time components to account for the timezone offset.
         * 
         * It applies the negative timezone offset to the date's time components to counter the eventual applied offset.
         * @returns A new Date object with the time offset removed
         */
        removeOffset(): Date;

        // Differentiate UTCdate and local Date
        isUTC?: boolean;
        toDateOnly(): DateOnly;
        toUTCDateTime(): UTCDate;
        utcOffset(offset: number | string, keepLocalTime?: boolean): Date | DateOnly;
    }

    interface DateConstructor {
        // Static Moment-like methods
        isMoment(obj: any): boolean;
        isDate(obj: any): boolean;
        isDuration(obj: any): boolean;
        parseZone(input: string): Moment;
        utc(inp?: moment.MomentInput, format?: moment.MomentFormatSpecification, language?: string, strict?: boolean): Moment;
        unix(timestamp: number): Moment;
        max(...dates: Date[]): Moment;
        min(...dates: Date[]): Moment;
    }
}

/// Core Moment Methods to Date prototype
Date.prototype.format = function (...args): string {
    return moment(this).format(...args);
};
Date.prototype.isValid = function (): boolean {
    return moment(this).isValid();
};
Date.prototype.clone = function () {
    return moment(this).clone();
};
Date.prototype.toDate = function () {
    return moment(this).toDate();
};
Date.prototype.unix = function () {
    return moment(this).unix();
};
Date.prototype.utc = function (...args) {
    return moment(this).utc(...args);
};
Date.prototype.local = function (...args) {
    return moment(this).local(...args);
};

// Manipulation methods
Date.prototype.add = function (...args) {
    return moment(this).add(...args);
};
Date.prototype.subtract = function (...args) {
    return moment(this).subtract(...args);
};
Date.prototype.startOf = function (...args) {
    return moment(this).startOf(...args);
};
Date.prototype.endOf = function (...args) {
    return moment(this).endOf(...args);
};

// Query methods
Date.prototype.isBefore = function (...args) {
    return moment(this).isBefore(...args);
};
Date.prototype.isAfter = function (...args): boolean {
    return moment(this).isAfter(...args);
};
Date.prototype.isSame = function (...args): boolean {
    return moment(this).isSame(...args);
};
Date.prototype.isBetween = function (...args): boolean {
    return moment(this).isBetween(...args);
};

// Difference methods
Date.prototype.diff = function (...args) {
    return moment(this).diff(...args);
};

// Get/Set methods with overloading
Date.prototype.year = function (value?: number) {
    if (value === undefined) {
        return this.getFullYear();
    }
    return moment(this).year(value);
};
Date.prototype.month = function (value?: number) {
    if (value === undefined) {
        return this.getMonth();
    }
    return moment(this).month(value);
};
Date.prototype.date = function (value?: number) {
    if (value === undefined) {
        return this.getDate();
    }
    return moment(this).date(value);
};
Date.prototype.day = function (value?: number) {
    if (value === undefined) {
        return this.getDay();
    }
    return moment(this).day(value);
};
Date.prototype.hour = function (value?: number) {
    if (value === undefined) {
        return this.getHours();
    }
    return moment(this).hour(value);
};
Date.prototype.minute = function (value?: number) {
    if (value === undefined) {
        return this.getMinutes();
    }
    return moment(this).minute(value);
};
Date.prototype.second = function (value?: number) {
    if (value === undefined) {
        return this.getSeconds();
    }
    return moment(this).second(value);
};
Date.prototype.millisecond = function (value?: number) {
    if (value === undefined) {
        return this.getMilliseconds();
    }
    return moment(this).millisecond(value);
};
Date.prototype.week = function (value?: number): any {
    if (value === undefined) {
        return moment(this).week();
    }
    return moment(this).week(value);
};
Date.prototype.removeOffset = function (): Date {
    let desiredTime = this.getTime() - (this.getTimezoneOffset() * 60000);
    return new Date(desiredTime);
};

// UTC handling
Date.prototype.isUTC = false;
Date.prototype.toDateOnly = function (): DateOnly {
    // cater for already DateOnly objects
    if (this instanceof DateOnly) return this;
    return new DateOnly(this);
};
Date.prototype.toUTCDateTime = function (): UTCDate {
    // cater for already UTCDate objects
    if (this instanceof UTCDate && !(this instanceof DateOnly)) return this;
    const applyOffset = (
        this.getHours() === 0 &&
        this.getMinutes() === 0 &&
        this.getSeconds() === 0 &&
        this.getMilliseconds() === 0
    )
    return new UTCDate(
        this instanceof DateOnly ? 
        this.valueOf() :
        (
            // we apply offset only if time components are all zero, to avoid double applying offsets
            applyOffset ?
            this.removeOffset() :
            this
        )
    );
}
Date.prototype.utcOffset = function (...args): Date | DateOnly {
    // DateOnly objects do not have a time/time zone, so return the value as-is...
    if (this instanceof DateOnly) return this;
    // Parse as UTC + straight pass-through to moment (without the mutation)...
    if (this.isUTC)
        return moment.utc(this).utcOffset(...args).toDate();

    // Straight pass-through to moment (without the mutation)...
    return moment(this).utcOffset(...args).toDate();
}

// Ensure UTCDate is UTC
UTCDate.prototype.isUTC = true;

// Static methods
Date.isMoment = function (obj: any): boolean {
    return moment.isMoment(obj) || obj instanceof Date;
};
Date.isDate = function (obj: any): boolean {
    return moment.isDate(obj) || obj instanceof Date;
};
Date.isDuration = function (obj: any): boolean {
    return moment.isDuration(obj);
};
Date.parseZone = function (input: string) {
    return moment.parseZone(input);
};
Date.utc = function (...args) {
    return moment.utc(...args);
};
Date.unix = function (...args) {
    return moment.unix(...args);
}
Date.max = function (...dates: Date[]) {
    return moment.max(dates.map(date => moment(date)));
}
Date.min = function (...dates: Date[]) {
    return moment.min(dates.map(date => moment(date)));
};

// need to consolidate string behavior
UTCDate.prototype.toString = function () {
    return this.toISOString();
};
window["UTCDate"] = UTCDate;